import * as THREE from 'three';
import { resolveBody } from './streetCollision';
import { footstep, thud, whoosh } from './sfx';

// ── A BODY UNDER THE CAMERA ──────────────────────────────────────────────────
//
// The street camera used to be a drone pinned at eye height: full speed the
// instant a key went down, dead stop the instant it came up, through every
// wall. This gives it the handful of things that make first person feel like
// someone walking:
//
//   · momentum — speed builds over a few tenths of a second and bleeds off the
//     same way, so turning a corner is a curve rather than a right angle;
//   · walls — the street's collision BVH pushes the body out of buildings,
//     posts and parked cars, and you slide along them instead of sticking;
//   · a gait — a step-timed dip and a slight shoulder roll, with a footstep on
//     each footfall; harder and faster when sprinting;
//   · a sprint that you can feel — the field of view opens up as you get going;
//   · a jump (Space), because a game you cannot jump in is a walking simulator.
//
// ?feel=classic restores the old instant, collision-free movement.

const params = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
export const FEEL_CLASSIC = params.get('feel') === 'classic';
const qn = (k, d) => { const v = parseFloat(params.get(k)); return Number.isFinite(v) && v > 0 ? v : d; };

// Street pace. The old 9 u/s walk was ~20 m/s in this world's scale (~2.2 m per
// unit) — a car's speed — which is a big part of why the city read as a model
// you float over. A brisk jog by default, the old pace as the sprint.
export const WALK_SPEED = qn('walkspeed', 4.2);
export const SPRINT_MULT = qn('sprint', 2.1);

const GRAVITY = 15;
const JUMP_V = 4.6;
const SPRINT_FOV = 7;   // degrees added at full sprint

export class WalkBody {
  constructor(camera) {
    this.vel = new THREE.Vector3();
    this.jumpY = 0;
    this.vy = 0;
    this.grounded = true;
    this.phase = 0;
    this.lastStep = 0;
    this.bobY = 0;
    this.roll = 0;
    this.fovBase = camera.fov;
    this.fovKick = 0;
    this.wasSprinting = false;
    this.prev = new THREE.Vector3();
  }

  jump() {
    if (!this.grounded) return;
    this.vy = JUMP_V;
    this.grounded = false;
    whoosh(0.5);
  }

  /**
   * Advance one frame. `wish` is the desired direction in the ground plane
   * (length ≤ 1, analog stick partials kept). Moves camera.position x/z,
   * returns the vertical offset to add on top of eye height and the roll.
   */
  step(dt, wish, sprinting, camera, { collide = true, pos = camera.position, gait = true } = {}) {
    dt = Math.min(dt, 0.05);
    const max = WALK_SPEED * (sprinting ? SPRINT_MULT : 1);
    const wanting = wish.lengthSq() > 1e-4;
    // Accelerate quickly, stop a touch more slowly — a body has weight.
    const rate = wanting ? 9 : 7.5;
    const k = 1 - Math.exp(-rate * dt);
    this.vel.x += (wish.x * max - this.vel.x) * k;
    this.vel.z += (wish.z * max - this.vel.z) * k;
    if (!wanting && this.vel.lengthSq() < 1e-4) this.vel.set(0, 0, 0);

    this.prev.copy(pos);
    pos.x += this.vel.x * dt;
    pos.z += this.vel.z * dt;
    if (collide && resolveBody(pos)) {
      // Keep only the velocity that actually happened: the part pointing into
      // the wall is gone, the part along it survives — you slide, not stick.
      this.vel.x = (pos.x - this.prev.x) / Math.max(dt, 1e-4);
      this.vel.z = (pos.z - this.prev.z) / Math.max(dt, 1e-4);
    }

    // Jump arc.
    if (!this.grounded) {
      this.vy -= GRAVITY * dt;
      this.jumpY += this.vy * dt;
      if (this.jumpY <= 0) {
        this.jumpY = 0; this.vy = 0; this.grounded = true;
        thud(Math.min(1, 0.4 + Math.hypot(this.vel.x, this.vel.z) / 10));
      }
    }

    // Gait: one dip per footfall, the shoulders rolling alternately.
    const speed = Math.hypot(this.vel.x, this.vel.z);
    const moving = this.grounded && speed > 0.6;
    if (moving) {
      const stepRate = 1.9 + speed * 0.17;            // steps per second
      this.phase += stepRate * dt;
      const step = Math.floor(this.phase);
      if (step !== this.lastStep) {
        this.lastStep = step;
        footstep({ run: speed > WALK_SPEED * 1.4, surface: 0.5 });
      }
    } else {
      // Settle back to a neutral stance rather than freezing mid-stride.
      const frac = this.phase - Math.floor(this.phase);
      this.phase += (frac < 0.5 ? -frac : 1 - frac) * Math.min(1, dt * 8);
    }
    const gaitAmt = Math.min(1, speed / WALK_SPEED);
    const s = Math.sin(Math.PI * this.phase);
    const ampY = speed > WALK_SPEED * 1.3 ? 0.032 : 0.02;   // a sprint bounces more
    const targetBob = moving ? -(Math.abs(s) - 0.5) * ampY * Math.min(1, gaitAmt * 1.3) : 0;
    this.bobY += (targetBob - this.bobY) * Math.min(1, dt * 18);
    const targetRoll = moving ? s * 0.007 * gaitAmt : 0;
    this.roll += (targetRoll - this.roll) * Math.min(1, dt * 10);

    // Sprint field-of-view.
    const sprintNow = sprinting && speed > WALK_SPEED * 1.25;
    if (sprintNow && !this.wasSprinting) whoosh(0.35);
    this.wasSprinting = sprintNow;
    const fovTarget = sprintNow ? SPRINT_FOV * Math.min(1, (speed - WALK_SPEED) / (max - WALK_SPEED + 1e-3)) : 0;
    this.fovKick += (fovTarget - this.fovKick) * Math.min(1, dt * 5);
    const fov = this.fovBase + this.fovKick;
    if (Math.abs(camera.fov - fov) > 0.01) { camera.fov = fov; camera.updateProjectionMatrix(); }

    // In third person the body carries the gait; the camera stays smooth.
    if (!gait) return { lift: this.jumpY, roll: 0, speed };
    return { lift: this.jumpY + this.bobY, roll: this.roll, speed };
  }

  /** Hand the camera back untouched (rides, cut-scenes). */
  release(camera) {
    this.vel.set(0, 0, 0);
    this.jumpY = 0; this.vy = 0; this.grounded = true;
    this.bobY = 0; this.roll = 0; this.fovKick = 0;
    if (Math.abs(camera.fov - this.fovBase) > 0.01) { camera.fov = this.fovBase; camera.updateProjectionMatrix(); }
  }
}
